import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { switchMap, map, catchError, pluck } from 'rxjs/operators';

import { AuthCredential } from '../actions';
import * as fromAuthActions from '../actions/auth.actions';
import { AuthService } from '../../services/auth.service';

@Injectable()
export class AuthEffects {
  actions$ = inject(Actions);
  authService = inject(AuthService);

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.AuthLogin),
      pluck('payload'),
      switchMap((resp: { email: string; password: string }) =>
        this.authService.login(resp).pipe(
          map((response: AuthCredential) =>
            fromAuthActions.AuthLoginSuccess({ payload: response }),
          ),
          catchError((error: any) => of(fromAuthActions.AuthLoginFail({ payload: error }))),
        ),
      ),
    ),
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.AuthLogout),
      pluck('payload'),
      switchMap(() =>
        this.authService.logout().pipe(
          map(() =>
            fromAuthActions.SetAuthCredentials({
              payload: {
                refreshToken: null,
                bearerToken: null,
              },
            }),
          ),
          catchError(() =>
            of(
              fromAuthActions.SetAuthCredentials({
                payload: {
                  refreshToken: null,
                  bearerToken: null,
                },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  refreshTokens$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.RefreshTokens),
      pluck('payload'),
      switchMap(() =>
        this.authService.getRefreshToken().pipe(
          map((bearerToken: string | null) =>
            fromAuthActions.SetAuthCredentials({ payload: { bearerToken } }),
          ),
          catchError((error: any) => of(fromAuthActions.AuthLoginFail(error))),
        ),
      ),
    ),
  );

  restorePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.RestorePassword),
      pluck('payload'),
      switchMap((payload: { emailAddress: string; confirmEmailAddress: string }) =>
        this.authService.restorePassword(payload.emailAddress, payload.confirmEmailAddress).pipe(
          map(() => fromAuthActions.RestorePasswordSuccess()),
          catchError((error: any) => of(fromAuthActions.AuthLoginFail(error))),
        ),
      ),
    ),
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.ResetPassword),
      pluck('payload'),
      switchMap((payload: any) =>
        this.authService
          .resetPassword(payload.password, payload.confirmPassword, payload.userKey)
          .pipe(
            map((resp) =>
              resp.errorMessageDto && resp.errorMessageDto.errorMessage
                ? fromAuthActions.ResetPasswordFail(resp.errorMessageDto)
                : fromAuthActions.ResetPasswordSuccess({ payload: { resetStatus: true } }),
            ),
            catchError((error: any) => of(fromAuthActions.AuthLoginFail(error))),
          ),
      ),
    ),
  );

  verifyUserURL = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.VerifyUser),
      pluck('payload'),
      switchMap((payload: any) =>
        this.authService.verifyUserToken(payload.userToken).pipe(
          map((res: any) =>
            fromAuthActions.VerifyUserSuccess({
              payload: {
                verificationStatus: res.message,
              },
            }),
          ),
          catchError((error: any) => of(fromAuthActions.VerifyUserFail(error.message))),
        ),
      ),
    ),
  );
}
